import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearCart, setPaymentStep } from "../../redux/slices/cart";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import PaymentStep from "../PaymentStep/PaymentStep";
import { useAddOrder } from "../../hooks/useAddOrder";
import { toast } from "react-toastify";
import "./OrderForm.css";

const validationSchema = Yup.object({
  billing: Yup.object({
    first_name: Yup.string().required("Required"),
    last_name: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    phone: Yup.string().required("Required"),
    address_1: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    state: Yup.string().required("Required"),
    postcode: Yup.string().required("Required"),
  }),
  line_items: Yup.array()
    .of(
      Yup.object({
        product_id: Yup.number().required("Required"),
        quantity: Yup.number().min(1).required("Required"),
        name: Yup.string(),
        authorStripeId: Yup.string(),
        total: Yup.string(),
      })
    )
    .min(1, "At least one product is required")
    .required(),
});

const OrderForm = () => {
  const token = useSelector((state) => state.auth.token);
  const { items, totalAmount, isPaymentStep } = useSelector((state) => state.cart);
  const [currentOrderData, setCurrentOrderData] = useState(null);
  const [orderResponse, setOrderResponse] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);
  const dispatch = useDispatch();
  const addOrderMutation = useAddOrder(token);

  useEffect(() => {
    if (items.length === 0 && isPaymentStep) {
      dispatch(setPaymentStep(false));
    }
  }, [items.length, isPaymentStep, dispatch]);

  const initialValues = {
    payment_method: "stripe",
    payment_method_title: "Credit Card (Stripe)",
    set_paid: false,
    billing: billingInfo || {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address_1: "",
      city: "",
      state: "",
      postcode: "",
      country: "US",
    },
    line_items: items.map((item) => ({
      product_id: item.id,
      quantity: item.quantity || 1,
      name: item.name,
      authorStripeID: item.authorStripeId,
      total: (parseFloat(item.price || 0) * (item.quantity || 1))
        .toFixed(2)
        .toString(),
    })),
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!token) {
      alert("You must be logged in to place an order");
      return;
    }

    // Save billing info for reuse
    setBillingInfo(values.billing);

    // Check if data has changed compared to last submitted order
    const dataHasChanged =
      !currentOrderData || 
      JSON.stringify(currentOrderData.billing) !== JSON.stringify(values.billing) ||
      JSON.stringify(currentOrderData.line_items) !== JSON.stringify(values.line_items);

    // If data hasn't changed and we have an existing order, reuse it
    if (!dataHasChanged && orderResponse) {
      console.log("Data unchanged, reusing existing order");
      dispatch(setPaymentStep(true));
      setSubmitting(false);
      return;
    }

    try {
      const response = await addOrderMutation.mutateAsync(values);

      // Store both the form data and the API response
      setCurrentOrderData(values);
      setOrderResponse(response.data);
      dispatch(setPaymentStep(true));
    } catch (error) {
      console.error("Order creation failed", error.response?.data || error);

      // More detailed error handling
      if (error.response?.data?.detail) {
        alert(`Order creation failed: ${error.response.data.detail}`);
      } else {
        alert("Order creation failed. Check console for details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    dispatch(setPaymentStep(false));
  };

  return (
    <section className="OrderForm">
      <div className="cart-total">
        <p>CART TOTAL:</p>
        <h1>
          <b>${totalAmount?.toFixed(2)}</b>
        </h1>
      </div>

      <p>BILLING DETAILS</p>

      {!isPaymentStep ? (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, submitForm }) => (
            <Form>
              <div className="form-row">
                <div className="billing-field">
                  <label>First Name</label>
                  <Field name="billing.first_name" />
                  <ErrorMessage
                    className="error"
                    name="billing.first_name"
                    component="div"
                  />
                </div>
                <div className="billing-field">
                  <label>Last Name</label>
                  <Field name="billing.last_name" />
                  <ErrorMessage
                    className="error"
                    name="billing.last_name"
                    component="div"
                  />
                </div>
              </div>

              <div className="billing-field">
                <label>Email</label>
                <Field name="billing.email" />
                <ErrorMessage
                  className="error"
                  name="billing.email"
                  component="div"
                />
              </div>

              <div className="billing-field">
                <label>Phone</label>
                <Field name="billing.phone" />
                <ErrorMessage
                  className="error"
                  name="billing.phone"
                  component="div"
                />
              </div>

              <div className="billing-field">
                <label>Address</label>
                <Field name="billing.address_1" />
                <ErrorMessage
                  className="error"
                  name="billing.address_1"
                  component="div"
                />
              </div>

              <div className="billing-field">
                <label>City</label>
                <Field name="billing.city" />
                <ErrorMessage
                  className="error"
                  name="billing.city"
                  component="div"
                />
              </div>

              <div className="form-row">
                <div className="billing-field">
                  <label>State</label>
                  <Field name="billing.state" />
                  <ErrorMessage
                    className="error"
                    name="billing.state"
                    component="div"
                  />
                </div>
                <div className="billing-field">
                  <label>Postcode</label>
                  <Field name="billing.postcode" />
                  <ErrorMessage
                    className="error"
                    name="billing.postcode"
                    component="div"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!items || items.length === 0) {
                    toast.error(
                      "Your cart is empty. Please add products to your cart."
                    );
                    return;
                  }
                  submitForm();
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating Order..." : "Continue to Payment"}
              </button>
              <button type="button" onClick={() => dispatch(clearCart())}>
                Clear Cart
              </button>
            </Form>
          )}
        </Formik>
      ) : (
        <PaymentStep
          orderData={{
            ...currentOrderData,
            orderId: orderResponse?.id,
            stripe_payment_intent_client_secret:
              orderResponse?.stripe_payment_intent_client_secret,
          }}
          onBack={handleBack}
        />
      )}
    </section>
  );
};

export default OrderForm;
